<template>
  <div
    id="site-app"
    :class="{
      'no-promo': !showPromo,
      'debug-screens': $store.getters.isLocalEnv,
      dunning: showDunningSnackbar,
      'promo-referral': showPromoReferral,
      'browse-page': isMarketplace,
      'promo-reactivation': hasReactivationPromo && isReactivationBarVisible,
      'holiday-promo': hasHolidayPromo,
    }"
  >
    <NavMenu />

    <div class="flex-1">
      <router-view />
    </div>

    <MainFooter />

    <LoginModal
      v-if="$store.state.openSignInModal"
      :social-error="socialError"
      @trackLoginEvent="trackLoginEvent"
    />
    <SignUpModal
      v-if="$store.state.openSignUpModal && !$store.getters.isLoggedIn"
      :social-error="socialError"
    />

    <DunningModal v-if="showDunningPopup" />
  </div>
</template>

<script>
import axios from 'axios';
import analyticsEvents from '@/utils/analyticsEvents';
import { updateUserInfo, eventTracker, identify } from '@/utils/eventTracker';
import getContext from '@/utilities/get-context';
import parseCategories from '@/utilities/parse-categories';
import DunningModal from '@/components/Dunning/dunning-modal.vue';
import Cookies from 'js-cookie';
import { SEARCH_LOCAL_STORAGE_TYPES, SESSION_NAMES, COOKIES } from '@/constants';
import gtmEvents from '@/utils/gtmEvents';
import { dataLayerTrack } from '@/utils/datalayer';
import { getBasicUserData } from '@/utils/gtmEventTracker';
import { setAnonymousID } from '@/utils/anonymousId';
import NavMenu from './components/NavMenu';
import MainFooter from './components/Footer';
import LoginModal from './components/LoginModal.vue';
import SignUpModal from './components/SignUpModal.vue';
import { setBaseSuperProperties, setMixpanelSuperProperties, addToSuperProperties } from './utils/eventSuperProperties';

export default {
  components: {
    NavMenu,
    MainFooter,
    LoginModal,
    SignUpModal,
    DunningModal,
  },

  provide() {
    return {
      categories: parseCategories(getContext().__MA__.browse_category_filters.categories),
      plans: getContext().__MA__.plans,
      hasIncentive: this.hasIncentive,
      hasPromo: this.hasPromo,
      hasHolidayPromo: this.hasHolidayPromo,
      hasReferralCode: () => this.hasReferralCode,
      hasExtraMonthsBenefit: () => this.hasExtraMonthsBenefit,
      hasReactivationPromo: () => this.hasReactivationPromo,
    };
  },
  data() {
    return {
      socialError: '',
    };
  },
  computed: {
    hasIncentive() {
      const hasReferralCode = this.referralCodeFromCookies?.length > 0;
      return (
        getContext().__MA__.incentive
        && (!hasReferralCode || this.hideReferralForMonthlyAndContinuityUsers)
      );
    },

    hasExtraMonthsBenefit() {
      return getContext().__MA__.benefit2Months && !this.hasReferralCode;
    },

    hasHolidayPromo() {
      return this.$store.state.subscription.isHolidayPromo && !this.hasIncentive;
    },

    hasReferralCode() {
      return (
        this.referralCode?.length > 0
        && !this.isAnnual
        && !this.hideReferralForMonthlyAndContinuityUsers
      );
    },

    hasReactivationPromo() {
      if (getContext().__MA__.billing_engine_subscription?.subscriptionReactivation?.showBanner) {
        addToSuperProperties({ user_status: 'expired' });
      }
      return (
        !this.showPromo
        && getContext().__MA__.billing_engine_subscription?.subscriptionReactivation?.showBanner
        && !this.isReactivatePromoClosedInCookie
      );
    },

    hasPromo() {
      // Todo: should be an environment variable but it's not setup properly.
      // Uncomment when turning off the promo until AWS Secrets is integrated to FE.
      // return false;

      // Hide promo for yearly plan users and team members.
      if (this.hasIncentive || this.hasHolidayPromo) {
        if (
          !this.$store.state.user?.plan?.includes('yearly')
          && !this.$store.state.user?.has_team
        ) {
          return true;
        }
      }

      return false;
    },

    showPromo() {
      const { path, fullPath } = this.$route;
      if (this.hasReferralCode || this.hasExtraMonthsBenefit) {
        return fullPath === '/pricing/';
      }
      if (this.hasPromo && this.hasHolidayPromo) {
        return fullPath === '/';
      }

      return (
        this.hasPromo && (path.startsWith('/pricing') || path.startsWith('/account/subscription'))
      );
    },
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    isDunning() {
      return this.$store.state.subscription?.isDunning;
    },
    isDunningSnackbarClosed() {
      return this.$store.state.subscription?.isDunningSnackbarClosed;
    },
    isDunningPopupClosed() {
      return this.$store.state.subscription?.isDunningPopupClosed;
    },
    showDunningPopup() {
      if (!this.isHasTeam || (this.isHasTeam && this.isAdmin)) {
        return this.isDunning && !this.isDunningPopupClosed;
      }
      return false;
    },
    showDunningSnackbar() {
      if (!this.isHasTeam || (this.isHasTeam && this.isAdmin)) {
        return this.isDunning && !this.isDunningSnackbarClosed;
      }
      return false;
    },
    isAdmin() {
      return this.$store.state.user?.is_team_admin;
    },
    isHasTeam() {
      return this.$store.state.user?.has_team;
    },
    isMarketplace() {
      return this.$route.path.startsWith('/browse');
    },
    showPromoReferral() {
      const isPricingPage = this.$route.fullPath === '/pricing/';
      return (
        isPricingPage
        && this.hasReferralCode
        && !this.isAnnual
        && !this.hideReferralForMonthlyAndContinuityUsers
      );
    },
    referralCode() {
      return this.$store.state.subscription.referralCode;
    },
    referralCodeFromCookies() {
      let cookieValueFor2MonthsBenefit = null;
      try {
        cookieValueFor2MonthsBenefit = JSON.parse(Cookies.get(COOKIES.NAMES.TWO_M_Benefit));
      } catch (e) {
        /* eslint-disable no-empty */
      }

      let cookieValueForReferralCode = Cookies.get(COOKIES.NAMES.REFERRAL_CODE);
      try {
        cookieValueForReferralCode = JSON.parse(Cookies.get(COOKIES.NAMES.REFERRAL_CODE));
      } catch (e) {
        // If referral code cookie is not using JSON format (that is using the format before 2 months benefit was
        // implmemented) then check if there is existing value for 2 months benefit cookie value, referral program
        // won't be used.
        if (cookieValueFor2MonthsBenefit) {
          return null;
        }
      }

      if (cookieValueForReferralCode && cookieValueFor2MonthsBenefit) {
        if (
          new Date(cookieValueFor2MonthsBenefit.created_at)
          > new Date(cookieValueForReferralCode.created_at)
        ) {
          return null;
        }
      }

      if (cookieValueForReferralCode?.code) {
        return cookieValueForReferralCode.code;
      }

      // Before implementing 2 months benefit, we were stored referral code directly as string, so we need
      // to support that case
      return cookieValueForReferralCode;
    },
    isReactivatePromoClosedInCookie() {
      return !!Cookies.get(COOKIES.NAMES.REACTIVATION_BAR_COOKIE);
    },
    userPlanName() {
      return this.isLoggedIn && this.$store.state.user.plan_name;
    },
    isAnnual() {
      return this.userPlanName === 'Annual';
    },
    // added because referral program does not support Monthly and Continuity plan yet
    hideReferralForMonthlyAndContinuityUsers() {
      return (
        this.isLoggedIn && (this.userPlanName === 'Monthly' || this.userPlanName === 'Continuity')
      );
    },
    isReactivationBarVisible() {
      return this.$store.state.isReactivationPromoVisible;
    },
    isVisitor() {
      return this.$store.getters.userType === 'visitor';
    },
    userCurrency() {
      return this.$store.state.currencyCode;
    },
    countryCode() {
      return this.$store.state.user.visiting_country;
    },
  },
  beforeDestroy() {
    window.removeEventListener(COOKIES.EVENTS.COOKIE_CONSENT_ALL, this.trackCookieScriptAccepted);
  },

  beforeMount() {
    window.__mixpanel__ = this.$mixpanel;
    window.__analyticsEvents__ = analyticsEvents;

    setBaseSuperProperties();
    setMixpanelSuperProperties(this.$mixpanel);
  },

  mounted() {
    const notFirstTimeSession = window.localStorage.getItem('notFirstTimeSession');
    const searchState = JSON.parse(
      window.localStorage.getItem(SEARCH_LOCAL_STORAGE_TYPES.SEARCH_STATE),
    );

    if (!notFirstTimeSession) {
      window.localStorage.setItem('notFirstTimeSession', '1');
      eventTracker(analyticsEvents.firstTimeVisit);
    }
    if (searchState) {
      this.$store.dispatch('search/setSearchParams', searchState);
    }
    setAnonymousID();
    updateUserInfo();

    sessionStorage.removeItem('redirectTo');

    this.setDunningSnackbarCloseStatus();
    this.setDunningPopupCloseStatus();
    this.setDunningStatus();

    this.initBrazeBIBridge();

    this.socialErrorHandle();
    this.trackEvents();

    this.setReferralCode();
    // Set currency for user
    this.getCurrency();
    this.setLeadInfo();
    window.addEventListener(COOKIES.EVENTS.COOKIE_CONSENT_ALL, this.trackCookieScriptAccepted);
  },
  methods: {
    setDunningStatus() {
      if (this.isLoggedIn) {
        const dunningStatus = getContext().__MA__.billing_engine_subscription?.is_dunning;

        this.$store.dispatch('setIsDunning', dunningStatus);
      }
    },
    setDunningSnackbarCloseStatus() {
      const sessionStorageValue = sessionStorage.getItem(SESSION_NAMES.IS_DUNNING_SNACKBAR_CLOSED);
      const isClose = sessionStorageValue === 'true';
      this.$store.dispatch('setIsDunningSnackbarClosed', isClose);
    },
    setDunningPopupCloseStatus() {
      const sessionStorageValue = sessionStorage.getItem(SESSION_NAMES.IS_DUNNING_POPUP_CLOSED);
      const isClose = sessionStorageValue === 'true';
      this.$store.dispatch('setIsDunningPopupClosed', isClose);
    },
    initBrazeBIBridge() {
      if (typeof window === 'undefined') {
        return;
      }

      window.addEventListener('message', (e) => {
        if (e.origin === window.location.origin) {
          const messageData = e.data;
          if (messageData?.type === 'trackEvent') {
            eventTracker(messageData.eventName, messageData.eventData);
          }
        }
      });
    },
    socialErrorHandle() {
      const urlParams = new URLSearchParams(window.location.search);
      const isSignUpError = urlParams.get('social_signup_error');
      const isLoginError = urlParams.get('social_login_error');
      const msg = urlParams.get('error_message');
      let modalType = '';
      let clickSource = '';
      if (isSignUpError) {
        modalType = 'setOpenSignUpModal';
        clickSource = 'sign up popup';
      }
      if (isLoginError) {
        modalType = 'setOpenSignInModal';
        clickSource = 'sign in popup';
      }
      if ((isSignUpError || isLoginError) && msg) {
        this.openModal(modalType, clickSource);
        this.$nextTick(() => {
          this.socialError = msg;
        });
      }
      if (this.$route.query['sign-up'] !== undefined) {
        this.openModal('setOpenSignUpModal', 'sign up popup');
      }
    },
    openModal(modalType, clickSource) {
      this.$store.dispatch(modalType, { open: true, clickSource });
    },
    trackEvents() {
      const urlParams = new URLSearchParams(window.location.search);
      const isLoginSuccessful = urlParams.get('social_login_successful') === '1';
      const isSignupSuccessful = urlParams.get('social_signup_successful') === '1';
      if (isLoginSuccessful) {
        this.trackLoginEvent(getContext().__MA__.user.id, true);
      } else if (isSignupSuccessful) {
        if (this.referralCodeFromCookies?.length > 0) {
          this.createReferralInvitation(this.referralCodeFromCookies);
        }
        this.trackSignupEvent();
      }
    },
    trackLoginEvent(userId = getContext().__MA__.user.id, trackOnce = false) {
      identify(userId);
      eventTracker(analyticsEvents.loginSuccess);
      const trackOnceUserId = trackOnce ? userId.toString() : null;
      dataLayerTrack({
        eventName: gtmEvents.login,
        data: getBasicUserData({ userId }),
        trackOnceUserId,
      });
    },
    trackSignupEvent() {
      const userId = getContext().__MA__.user.id;
      const { email } = getContext().__MA__.user;
      const method = 'social';
      eventTracker(analyticsEvents.register, {
        user_id: userId,
        method,
        registration_method: method,
        user_agent: navigator.userAgent,
        referred_url: document.referrer,
      });

      const userBasicData = getBasicUserData({
        userId,
        email,
      });
      const trackOnceUserId = (getContext().__MA__.user.id || '').toString();
      dataLayerTrack({ eventName: gtmEvents.completeSignup, data: userBasicData, trackOnceUserId });
    },
    setReferralCode() {
      if (this.referralCodeFromCookies) {
        this.$store.dispatch('setReferralCode', this.referralCodeFromCookies);
      }
    },
    async createReferralInvitation(code) {
      try {
        await axios.post('/api/v3/referral/', {
          code,
          is_sign_up_free: true,
        });
      } catch (error) {
        console.log('social signup createReferralInvitation error', { error });
      }
    },
    getCurrency() {
      this.$store.dispatch('setCurrencyCode', this.userCurrency);

      return this.userCurrency;
    },

    setLeadInfo() {
      if (typeof window === 'undefined') {
        return;
      }
      const leadInfoClassification = Cookies.get(COOKIES.NAMES.LEAD_INFO_CLASSIFICATION);
      const leadInfoJourney = Cookies.get(COOKIES.NAMES.LEAD_INFO_JOURNEY);
      if (leadInfoClassification) {
        this.$store.dispatch('setUserLeadType', leadInfoClassification);
        addToSuperProperties({ lead_type_indication: leadInfoClassification });
      }
      if (leadInfoJourney?.length > 0) {
        this.$store.dispatch('setUserLeadInfoJourney', JSON.parse(leadInfoJourney));
        addToSuperProperties({ journey: JSON.parse(leadInfoJourney) });
      }
    },
    trackCookieScriptAccepted() {
      eventTracker(analyticsEvents.cookieAccepted);
    },
  },
};
</script>

<style scoped>
#site-app {
  display: flex;
  flex-direction: column;

  min-height: 100vh;

  /**
  * This allows percentage height to work down the DOM tree.
  * We have to set a deterministic height so percentage height works
  * instead of the initial value of "auto".
  * The min-height will be the starting height.
  */
  height: 0;

  /**
   * 45px = NavMenu height
   * 164px = NavMenu + PromoHoliday (with graphics)
   * 125px = NavMenu + PromoIncentive
  */
  padding-top: 125px;
}

#site-app.no-promo {
  padding-top: 45px;
}
#site-app.dunning {
  padding-top: 200px;
}
#site-app.promo-referral {
  padding-top: 138px;
}
#site-app.promo-reactivation {
  padding-top: 178px;
}
@screen sm {
  #site-app.promo-reactivation {
    padding-top: 154px;
  }
  #site-app.promo-reactivation.holiday-promo {
    padding-top: 89px;
  }
}
@screen md {
  #site-app.dunning {
    padding-top: 180px;
  }
}
@screen lg {
  #site-app.dunning {
    padding-top: 60px;
  }
  #site-app.promo-referral {
    padding-top: 109px;
  }

  #site-app.no-promo,
  #site-app.holiday-promo,
  #site-app.no-promo.holiday-promo {
    padding-top: 122px;
  }
  #site-app.promo-reactivation {
    padding-top: 196px;
  }
  #site-app.promo-reactivation.holiday-promo {
    padding-top: 176px;
  }
}
@screen xl {
  #site-app.promo-reactivation {
    padding-top: 198px;
  }
  #site-app.promo-reactivation.holiday-promo {
    padding-top: 179px;
  }
}
</style>
